<template>
    <div class="exploration-wrapper pt-0" ref="explorationWrap">
        <div class="fd-dm-exploration-body">
            <PreDefinedFilters :drawerValue="drawerValue" @showFilterDiv="showFilterDiv"
                @getFilterDetailTopage="getFilterDetailTopage" ref="preFilter" />
            <TableData
                ref="tableData"
                :page="currentPage"
                :items-per-page="currentItemsPerPage"
                @resetFiltersEmitted="resetFiltersEmitted"
                :filterData="filterData"
                :isLoading="isLoading"
                :filterDetails="filterDetails"
                :showFilter="showFilter"
                @update:pagination="handlePaginationUpdate"
                @getFilterFromTable="getFilterFromTable"
            />
            <div class="exploration-pagination-wrap">
                <Pagination
                    variant="scorecard"
                    :total-items="paginationTotalRecords"
                    v-model:current-page="currentPage"
                    v-model:items-per-page="currentItemsPerPage"
                    item-label="Samples"
                    :page-sizes="[20, 50, 100, 200, 300, 400, 500]"
                    @update:current-page="onExplorationPaginationChange"
                    @update:items-per-page="onExplorationPaginationItemsPerPage"
                />
            </div>
        </div>
        <div class="filter-wrapper">
            <Filters v-if="isDataExploration" @drawerStateChanged="handleRightDrawerStateChange"
                @filterApplied="applyFilter" @resetFiltersEmittedUpdated="resetFiltersEmittedUpdated" @drawerValue="holdDrawerValue"
                @updateFilter="callSummaryAgain" ref="filters" />
        </div>
    </div>
    <div v-if="timeoutMessage != ''" class="no-asset-div timeout">
        <div class="text-center">
          {{ timeoutMessage }}
          <img :src="refreshIcon" alt="" width="24" height="24" class="fd-action-icon" @click="fetchFilterData()" />
        </div>
      </div>
</template>

<script>
import PreDefinedFilters from "@/components/DataExploration/PreDefinedFilters.vue";
import TableData from "@/components/DataExploration/TableData.vue";
import Filters from "@/components/layout/Common/Filters.vue";
import Pagination from "@/components/layout/Common/Pagination.vue";
import screenWidthMixin from "../assets/JS/screenWidthMixin";
import api from "@/axiosInterceptor";
import { mapGetters, mapActions } from "vuex";

export default {
    components: {
        PreDefinedFilters,
        TableData,
        Filters,
        Pagination,
    },
    name: "DataExploration",
    mixins: [screenWidthMixin],
    data: () => ({
        showFilter: true,
        filterData: null,
        currentFilterId: null,
        isLoading: true,
        currentPage: 1,
        currentItemsPerPage: Number(localStorage.getItem('itemsPerPage')) || 20,
        filterDetails: null, // Added to store filter details
        isFilterChanged: false, // Added to track if filter has been changed
        drawerValue: false,
        sortColumn: "",
        sortDirection: "",
        timeoutMessage: "",
        refreshIcon: require("../assets/Svg/Icons/refresh-2.svg"),
        filterDataAbortController: null,
        filterDetailsAbortController: null,
        headingResizeObserver: null
    }),
    computed: {
        ...mapGetters("tenantId", ["getSelectedTenant"]),
        isDataExploration() {
    const pathSegments = this.$route.path.split('/').filter(Boolean);
    return pathSegments.length > 1 && pathSegments[1] === 'data-management';
},
        paginationTotalRecords() {
            return this.filterData?.pagination?.TotalRecords ?? 0;
        },
    },
    methods: {
        ...mapActions("tenantId", ["getUserData"]),
        cancelFilterDataRequest() {
            if (this.filterDataAbortController) {
                this.filterDataAbortController.abort();
                this.filterDataAbortController = null;
            }
        },
        cancelFilterDetailsRequest() {
            if (this.filterDetailsAbortController) {
                this.filterDetailsAbortController.abort();
                this.filterDetailsAbortController = null;
            }
        },
        cancelAllRequests() {
            this.cancelFilterDataRequest();
            this.cancelFilterDetailsRequest();
        },
        holdDrawerValue(val) {
            this.drawerValue = val;
        },
        syncCharacterizationHeadingHeight() {
            const wrap = this.$refs.explorationWrap;
            const heading = wrap && wrap.querySelector(".table-top-content");
            if (!wrap || !heading) return;
            wrap.style.setProperty("--fd-dm-char-heading-h", `${heading.offsetHeight}px`);
        },
        bindCharacterizationHeadingHeight() {
            this.unbindCharacterizationHeadingHeight();
            const heading = this.$refs.explorationWrap && this.$refs.explorationWrap.querySelector(".table-top-content");
            if (!heading || typeof ResizeObserver === "undefined") {
                this.syncCharacterizationHeadingHeight();
                return;
            }
            this.headingResizeObserver = new ResizeObserver(() => {
                this.syncCharacterizationHeadingHeight();
            });
            this.headingResizeObserver.observe(heading);
            this.syncCharacterizationHeadingHeight();
        },
        unbindCharacterizationHeadingHeight() {
            if (this.headingResizeObserver) {
                this.headingResizeObserver.disconnect();
                this.headingResizeObserver = null;
            }
        },
        async getFilterDetailTopage(filter) {
            this.$refs.filters.resetFilter();
            this.currentFilterId = filter.ID;
            localStorage.setItem('lastFilterId', filter.ID);
            this.currentPage = 1; // Reset to first page when filter changes
            this.isFilterChanged = true; // Mark filter as changed
            await this.fetchFilterDetails(); // Changed to fetch filter details first
            this.saveFilterDetails(); // Save filter details to localStorage
        },

        async showFilterDiv(val) {
            this.showFilter = val;
        },

        async fetchFilterDetails() {
            this.cancelFilterDetailsRequest();
            
            this.filterDetailsAbortController = new AbortController();
            const controller = this.filterDetailsAbortController;
            const signal = controller.signal;
            
            this.isLoading = true;
            let startedFilterDataFetch = false;
            try {
                const response = await api.get(`public/api/filters/${this.currentFilterId}`, { signal });
                if (response && response.data && (response.data.statusCode === 200 || response.data.statusCode === 201)) {
                    this.filterDetails = response.data.data.filter; // Store filter details
                    this.saveFilterDetails(); // Save filter details to localStorage
                    startedFilterDataFetch = true;
                    this.fetchFilterData(); // Call fetchFilterData after getting filter details
                }
                else if (response && response.data && response.data.message) {
                    console.error(response.data.message);
                }
                else {
                    console.error("Error fetching filter details.");
                }
            } catch (error) {
                if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
                    return;
                }
                console.error("Error fetching filter details:", error);
            } finally {
                if (this.filterDetailsAbortController === controller) {
                    this.filterDetailsAbortController = null;
                    if (!startedFilterDataFetch) {
                        this.isLoading = false;
                    }
                }
            }
        },
        async getFilterFromTable(filterData) {
            if (filterData.withFilter) {
                this.filterDetails = filterData;
            } else {
                this.filterDetails = {
                    asset: filterData.assets,
                    field: filterData.fields,
                    well: filterData.wells,
                    reservoir: filterData.reservoirs,
                    fluidType: filterData.fluidType,
                    selectedTests: filterData.tests,
                    fluidSampleID: filterData.fluidSampleID, // Added fluidSampleID column
                };
            }
            this.sortColumn = filterData.sortColumn;
            this.sortDirection = filterData.sortDirection;
            this.$refs.filters.populateFieldsFromFilter(this.filterDetails);
            this.saveFilterDetails(); // Save filter details to localStorage
            await this.fetchFilterData();
        },
        
        async fetchFilterData() {
            this.cancelFilterDataRequest();
            
            this.filterDataAbortController = new AbortController();
            const controller = this.filterDataAbortController;
            const signal = controller.signal;
            
            this.isLoading = true;
            this.timeoutMessage ="";
            try {
                const params = {
                    pageSize: this.currentItemsPerPage,
                    pageNumber: this.currentPage,
                    viewSummary: true
                };
                if (this.filterDetails) {
                    params.field = this.filterDetails.field?.length ? `["${this.filterDetails.field.join('","')}"]` : '[]';
                    params.well = this.filterDetails.well?.length ? `["${this.filterDetails.well.join('","')}"]` : '[]';
                    params.asset = this.filterDetails.asset?.length ? `["${this.filterDetails.asset.join('","')}"]` : '[]';
                    params.reservoir = this.filterDetails.reservoir?.length ? `["${this.filterDetails.reservoir.join('","')}"]` : '[]';
                    params.fluidType = this.filterDetails.fluidType?.length ? `["${this.filterDetails.fluidType.join('","')}"]` : '[]';
                    params.sampleType = this.filterDetails.sampleType?.length ? `["${this.filterDetails.sampleType.join('","')}"]` : '[]';
                    params.fluidClassification = this.filterDetails.fluidClassification?.length ? `["${this.filterDetails.fluidClassification.join('","')}"]` : '[]';
                    params.selectedTests = this.filterDetails.selectedTests?.length
                        ? `["${this.filterDetails.selectedTests.map(test => test.toLowerCase().replace(/\s+/g, '')).join('","')}"]`
                        : '[]';
                    if (this.sortColumn) {
                        params.sortColumn = `"${this.sortColumn.replace(/s$/, "")}"`;
                    }

                    if (this.sortDirection) {
                        params.sortDirection = `"${this.sortDirection}"`;
                    }
                    if (this.filterDetails.fluidSampleID?.length) {
                        params.fluidSampleID = `["${this.filterDetails.fluidSampleID.join('","')}"]`;
                    }
                }else{
                    this.$refs.filters.resetFilter();
                }
                const response = await api.get(`public/api/samples`, { params, signal });
                if (this.filterDataAbortController !== controller) {
                    return;
                }
                if (response && response.data && (response.data.statusCode === 200 || response.data.statusCode === 201)) {
                    this.filterData = response.data.data; // Update filterData with new data
                    if (response.data.data.samples == null) { // Check if no data is available
                        
                       this.filterData = null; // Set filterData to null to indicate no data found
                   }
                    if (this.filterDetails && this.filterDetails.name) {
                        this.$refs.filters.onSelectFilterGet(this.filterDetails.name);
                    }
                   
                    
                }
                else if (response && response.data && response.data.message) {
                    this.timeoutMessage = response.data.message;
                }
                else {
                    this.timeoutMessage = "Error fetching filter details.";
                }
            } catch (error) {
                if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
                    return;
                }
                this.timeoutMessage = error;
            } finally {
                if (this.filterDataAbortController === controller) {
                    this.isLoading = false;
                    this.filterDataAbortController = null;
                }
            }
        },
        handleDrawerStateChange(isOpen) {
      if (this.screenWidth >= 768) {
        this.drawerOpen = isOpen;
      }
    },
        async handlePaginationUpdate({ page, itemsPerPage }) {
            this.currentPage = page;
            this.currentItemsPerPage = itemsPerPage;
            if (this.isFilterChanged) { // Check if filter has been changed
                if (!this.filterDetails || Object.keys(this.filterDetails).every(key => this.filterDetails[key].length === 0)) {
                    await this.fetchFilterData(); // Call fetchFilterData without filter if all filter fields are empty
                } else {
                    if (this.currentFilterId && !this.filterDetails.ID) {
                        await this.fetchFilterDetails();
                    } else {
                        this.fetchFilterData();
                    }
                }
            } else {
                await this.fetchFilterData(); // Call fetchFilterData directly if filter has not been changed
            }
        },
        async onExplorationPaginationChange() {
            await this.handlePaginationUpdate({
                page: this.currentPage,
                itemsPerPage: this.currentItemsPerPage,
            });
        },
        async onExplorationPaginationItemsPerPage() {
            this.currentPage = 1;
            await this.handlePaginationUpdate({
                page: 1,
                itemsPerPage: this.currentItemsPerPage,
            });
        },

        async resetFiltersEmitted() {
            this.currentFilterId = null;
            localStorage.removeItem('lastFilterId');
            localStorage.removeItem('dataManagementFilterDetails'); // Clear saved filter details
            this.currentPage = 1; // Reset to first page after filter reset
            this.currentItemsPerPage = 20; // Reset to default items per page
            this.isFilterChanged = false;
            this.filterDetails = null // Reset filter change flag after filter reset
            this.$refs.filters.resetFilter(); // Call reset function in Filters component
            this.$refs.preFilter.resetCardSelection();
            if (this.$refs.tableData && typeof this.$refs.tableData.resetTableFilter === 'function') {
                this.$refs.tableData.resetTableFilter(); // Example function
            }
            await this.fetchFilterData(); // Call fetchFilterData to reset the table data
        },
        async resetFiltersEmittedUpdated() {
            this.filterDetails = null
            this.currentFilterId = null;
            localStorage.removeItem('dataManagementFilterDetails'); // Clear saved filter details
            this.currentPage = 1; // Reset to first page after filter reset
            this.currentItemsPerPage = 20; // Reset to default items per page
            if (this.$refs.tableData && typeof this.$refs.tableData.resetTableFilter === 'function') {
                this.$refs.tableData.resetTableFilter(); // Example function
            }
            this.$refs.preFilter.resetCardSelection();
            await this.fetchFilterData(); // Call fetchFilterData to reset the table data
        },

        async applyFilter(filterDetails) {
            this.filterDetails = filterDetails.filterData; // Update filterDetails with the new filter
            this.isFilterChanged = true; // Mark filter as changed
            this.currentPage = 1; // Reset to first page when filter changes
            this.$refs.preFilter.CardSelection(filterDetails.filterData);
            this.saveFilterDetails(); // Save filter details to localStorage
            await this.fetchFilterData(); // Fetch new data based on the applied filter
        },
        callSummaryAgain() {
            this.$refs.preFilter.getFilterSummaries();
        },
        
        saveFilterDetails() {
            if (this.filterDetails) {
                localStorage.setItem('dataManagementFilterDetails', JSON.stringify(this.filterDetails));
            }
        },
    },
    watch: {
  '$route'(to, from) {
    if (to.fullPath !== from.fullPath) {
        this.cancelAllRequests();
        this.filterData = null;
        this.currentPage = 1;
        this.currentItemsPerPage = 20;
        this.$nextTick(() => {
          if (this.$refs.tableData && typeof this.$refs.tableData.resetTableFilter === 'function') {
            this.$refs.tableData.resetTableFilter();
          }
        });
        this.fetchFilterData();
        this.getUserData();
    }
  },
  getSelectedTenant: {
    handler(newVal, oldVal) {
      if (oldVal && newVal && newVal.ID !== oldVal.ID) {
        this.cancelAllRequests();
        this.filterData = null;
        localStorage.removeItem('lastFilterId');
        localStorage.removeItem('dataManagementFilterDetails');
        localStorage.removeItem('selectedFilterId');
        localStorage.removeItem('pendingFilterData');
        localStorage.removeItem('filterActive');
        localStorage.removeItem('filterApplied');
        localStorage.removeItem('defaultFilter');
        localStorage.removeItem('savedChartData');
        this.currentFilterId = null;
        this.filterDetails = null;
        this.isFilterChanged = false;
        this.currentPage = 1;
        this.currentItemsPerPage = 20;
        this.sortColumn = "";
        this.sortDirection = "";
        this.$nextTick(() => {
          if (this.$refs.filters) this.$refs.filters.resetFilter();
          if (this.$refs.preFilter) this.$refs.preFilter.resetCardSelection();
          if (this.$refs.tableData && typeof this.$refs.tableData.resetTableFilter === 'function') {
            this.$refs.tableData.resetTableFilter();
          }
          this.fetchFilterData();
        });
      }
    },
    deep: true,
    immediate: false
  }
  },
    mounted() {
     this.getUserData();
        this.$nextTick(() => this.bindCharacterizationHeadingHeight());
        const lastFilterId = localStorage.getItem('lastFilterId');
        const savedFilterDetails = localStorage.getItem('dataManagementFilterDetails');
        
        if (lastFilterId) {
            this.currentFilterId = lastFilterId;
            this.isFilterChanged = true; // Mark filter as changed on component creation if lastFilterId exists
            this.fetchFilterDetails(); // Fetch filter details on component creation if lastFilterId exists
        } else if (savedFilterDetails) {
            try {
                this.filterDetails = JSON.parse(savedFilterDetails);
                this.isFilterChanged = true;
                this.$refs.filters.populateFieldsFromFilter(this.filterDetails);
                this.fetchFilterData();
            } catch (error) {
                console.error('Error parsing saved filter details:', error);
                this.$refs.filters.resetFilter();
                this.fetchFilterData();
            }
        } else {
            this.$refs.filters.resetFilter();
            this.fetchFilterData(); // Call fetchFilterData on component creation to fetch data without filters if lastFilterId does not exist
        }
    },
    async beforeUnmount() {
        this.unbindCharacterizationHeadingHeight();
        this.cancelAllRequests();
    }
};
</script>